'use client'

import { useState, useRef } from 'react'
import Header from '@/components/header'
import {
  ArrowLeft,
  Upload,
  Download,
  Image as ImageIcon,
  Sparkles,
  X,
  Check,
  Loader2,
  Info,
  Archive,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import Api from '@/api/index'

interface CompressedImage {
  filename: string
  savedFilename: string
  originalSize: number
  compressedSize: number
  compressedData: string
  ratio: string
  success: boolean
  error?: string
}

interface CompressResponse {
  code: number
  data: {
    results: CompressedImage[]
    quota: {
      used: number
      remaining: number
      total: number
    }
    totalImages: number
    successCount: number
    failCount: number
  }
  message: string
}

export default function TinifyPage() {
  const router = useRouter()
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [compressedImages, setCompressedImages] = useState<CompressedImage[]>(
    [],
  )
  const [isCompressing, setIsCompressing] = useState(false)
  const [quota, setQuota] = useState<{
    used: number
    remaining: number
    total: number
  } | null>(null)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 处理文件选择
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])

    // 验证文件类型
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} 不是有效的图片文件`)
        return false
      }
      return true
    })

    setSelectedFiles((prev) => [...prev, ...validFiles])
  }

  // 移除文件
  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  // 清空所有
  const clearAll = () => {
    setSelectedFiles([])
    setCompressedImages([])
    setQuota(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // 压缩图片
  const compressImages = async () => {
    if (selectedFiles.length === 0) {
      alert('请先选择图片')
      return
    }

    setIsCompressing(true)

    try {
      const formData = new FormData()
      selectedFiles.forEach((file) => {
        formData.append('images', file)
      })

      const response = await Api.h5.toolsCompressImages(formData)

      const result: any = response

      if (result.code === 200) {
        setCompressedImages(result.data.results)
        setQuota(result.data.quota)
        // 清空选择的文件
        setSelectedFiles([])
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } else {
        alert(result.message || '压缩失败')
      }
    } catch (error) {
      console.error('压缩失败:', error)
      alert('压缩失败，请重试')
    } finally {
      setIsCompressing(false)
    }
  }

  // 下载单张图片
  const downloadSingle = (image: CompressedImage) => {
    const link = document.createElement('a')
    link.href = image.compressedData
    link.download = `compressed_${image.filename}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // 批量下载（打包成zip）
  const downloadAll = async () => {
    if (compressedImages.length === 0) return

    const zip = new JSZip()
    const successImages = compressedImages.filter((img) => img.success)

    successImages.forEach((image) => {
      // 将base64转换为blob
      const base64Data = image.compressedData.split(',')[1]
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'image/png' })

      zip.file(`compressed_${image.filename}`, blob)
    })

    const content = await zip.generateAsync({ type: 'blob' })
    saveAs(content, `compressed_images_${Date.now()}.zip`)
  }

  // 格式化文件大小
  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <>
      <Header />

      <main className='min-h-screen bg-linear-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden'>
        {/* 背景装饰 */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-slow' />
          <div
            className='absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow'
            style={{ animationDelay: '2s' }}
          />
          <div
            className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse-slow'
            style={{ animationDelay: '4s' }}
          />
        </div>

        {/* 内容区域 */}
        <div className='relative max-w-7xl mx-auto px-4 py-12'>
          {/* 返回按钮 */}
          <button
            onClick={() => router.push('/light')}
            className='group mb-8 flex items-center gap-2 px-4 py-3 rounded-2xl bg-card/80 backdrop-blur-xl border-2 border-border hover:border-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-fade-in'
          >
            <div className='relative'>
              <div className='absolute inset-0 bg-primary/20 rounded-full blur-md scale-0 group-hover:scale-150 transition-transform duration-500' />
              <ArrowLeft className='relative w-5 h-5 text-primary group-hover:text-primary transition-all duration-300 group-hover:-translate-x-1' />
            </div>
            <span className='text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-300'>
              返回
            </span>
          </button>

          {/* 主卡片 */}
          <div className='bg-card/80 backdrop-blur-xl rounded-3xl border-2 border-border shadow-2xl p-8 animate-fade-in-up'>
            {/* 标题区域 */}
            <div className='text-center mb-8'>
              <div className='inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-linear-to-br from-primary to-secondary mb-4 shadow-lg relative overflow-hidden group'>
                <div className='absolute inset-0 bg-linear-to-br from-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
                <Sparkles className='relative w-8 h-8 text-primary-foreground animate-pulse' />
              </div>
              <h1 className='text-3xl font-bold bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent mb-2'>
                图片智能压缩
              </h1>
              <p className='text-muted-foreground'>
                基于 TinyPNG 算法，无损压缩图片大小，支持批量处理
              </p>
            </div>

            {/* 配额信息 */}
            {quota && (
              <div className='mb-6 p-4 rounded-xl bg-muted/50 backdrop-blur-sm border border-border/50 animate-fade-in'>
                <div className='flex items-center justify-between mb-2'>
                  <div className='flex items-center gap-2'>
                    <Info className='w-4 h-4 text-primary' />
                    <span className='text-sm font-medium text-foreground'>
                      API 配额
                    </span>
                  </div>
                  <div className='flex items-center gap-4 text-sm'>
                    <span className='text-muted-foreground'>
                      已使用:{' '}
                      <span className='font-semibold text-foreground'>
                        {quota.used}
                      </span>
                    </span>
                    <span className='text-muted-foreground'>
                      剩余:{' '}
                      <span className='font-semibold text-primary'>
                        {quota.remaining}
                      </span>
                    </span>
                    <span className='text-muted-foreground'>
                      总计:{' '}
                      <span className='font-semibold text-foreground'>
                        {quota.total}
                      </span>
                    </span>
                  </div>
                </div>
                <div className='w-full h-2 bg-muted rounded-full overflow-hidden'>
                  <div
                    className='h-full bg-linear-to-r from-primary to-secondary transition-all duration-500'
                    style={{ width: `${(quota.used / quota.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* 上传区域 */}
            <div className='space-y-6'>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                multiple
                onChange={handleFileSelect}
                className='hidden'
              />

              <div
                onClick={() => fileInputRef.current?.click()}
                className='group relative border-2 border-dashed border-border hover:border-primary rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 bg-muted/20 hover:bg-muted/40'
              >
                <div className='absolute inset-0 bg-linear-to-br from-primary/5 to-secondary/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
                <div className='relative'>
                  <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 group-hover:bg-primary/20 mb-4 transition-colors duration-300'>
                    <Upload className='w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300' />
                  </div>
                  <p className='text-foreground font-medium mb-1'>
                    点击上传图片或拖拽到此处
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    支持 JPG、PNG、GIF、WebP、BMP 格式，支持批量上传
                  </p>
                </div>
              </div>

              {/* 已选择的文件列表 */}
              {selectedFiles.length > 0 && (
                <div className='space-y-3 animate-fade-in'>
                  <div className='flex items-center justify-between'>
                    <h3 className='text-sm font-semibold text-foreground flex items-center gap-2'>
                      <ImageIcon className='w-4 h-4 text-primary' />
                      待压缩图片 ({selectedFiles.length})
                    </h3>
                    <button
                      onClick={clearAll}
                      className='text-sm text-muted-foreground hover:text-destructive transition-colors duration-200'
                    >
                      清空全部
                    </button>
                  </div>
                  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className='relative group rounded-xl border border-border bg-muted/20 p-3 hover:shadow-md transition-all duration-200'
                      >
                        <div className='flex items-center gap-3'>
                          <div className='flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center'>
                            <ImageIcon className='w-6 h-6 text-primary' />
                          </div>
                          <div className='flex-1 min-w-0'>
                            <p className='text-sm font-medium text-foreground truncate'>
                              {file.name}
                            </p>
                            <p className='text-xs text-muted-foreground'>
                              {formatSize(file.size)}
                            </p>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className='flex-shrink-0 p-1 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors duration-200'
                          >
                            <X className='w-4 h-4' />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 压缩按钮 */}
              {selectedFiles.length > 0 && (
                <button
                  onClick={compressImages}
                  disabled={isCompressing}
                  className='w-full group relative overflow-hidden rounded-2xl bg-linear-to-r from-primary to-secondary p-0.5 hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <div className='relative bg-card/90 backdrop-blur-sm rounded-2xl px-6 py-4 group-hover:bg-transparent transition-all duration-300'>
                    <div className='flex items-center justify-center gap-2 text-foreground group-hover:text-primary-foreground transition-colors duration-300'>
                      {isCompressing ? (
                        <>
                          <Loader2 className='w-5 h-5 animate-spin' />
                          <span className='font-medium'>压缩中...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className='w-5 h-5' />
                          <span className='font-medium'>
                            开始压缩 ({selectedFiles.length} 张)
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              )}

              {/* 压缩结果 */}
              {compressedImages.length > 0 && (
                <div className='space-y-4 animate-fade-in'>
                  <div className='flex items-center justify-between'>
                    <h3 className='text-sm font-semibold text-foreground flex items-center gap-2'>
                      <Check className='w-4 h-4 text-green-500' />
                      压缩完成 (
                      {compressedImages.filter((img) => img.success).length}/
                      {compressedImages.length})
                    </h3>
                    <button
                      onClick={downloadAll}
                      className='group flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-all duration-300 hover:scale-105'
                    >
                      <Archive className='w-4 h-4' />
                      <span>批量下载</span>
                    </button>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {compressedImages.map((image, index) => (
                      <div
                        key={index}
                        className={`relative rounded-xl border-2 ${
                          image.success
                            ? 'border-green-500/20 bg-green-500/5'
                            : 'border-destructive/20 bg-destructive/5'
                        } p-4`}
                      >
                        {image.success ? (
                          <>
                            {/* 图片预览 */}
                            <div
                              className='relative w-full h-48 rounded-lg bg-muted/50 mb-3 overflow-hidden cursor-pointer'
                              onClick={() =>
                                setPreviewImage(image.compressedData)
                              }
                            >
                              <img
                                src={image.compressedData}
                                alt={image.filename}
                                className='w-full h-full object-contain hover:scale-105 transition-transform duration-300'
                              />
                            </div>

                            {/* 文件信息 */}
                            <div className='space-y-2'>
                              <p className='text-sm font-medium text-foreground truncate'>
                                {image.filename}
                              </p>
                              <div className='flex items-center justify-between text-xs text-muted-foreground'>
                                <span>
                                  原始: {formatSize(image.originalSize)}
                                </span>
                                <span className='text-green-500 font-semibold'>
                                  -{image.ratio}
                                </span>
                                <span>
                                  压缩: {formatSize(image.compressedSize)}
                                </span>
                              </div>
                              <div className='w-full h-1.5 bg-muted rounded-full overflow-hidden'>
                                <div
                                  className='h-full bg-linear-to-r from-green-500 to-green-600 transition-all duration-500'
                                  style={{
                                    width: `${100 - parseFloat(image.ratio)}%`,
                                  }}
                                />
                              </div>
                            </div>

                            {/* 下载按钮 */}
                            <button
                              onClick={() => downloadSingle(image)}
                              className='mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-all duration-300 hover:scale-105'
                            >
                              <Download className='w-4 h-4' />
                              <span>下载</span>
                            </button>
                          </>
                        ) : (
                          <div className='flex items-center gap-3'>
                            <X className='w-8 h-8 text-destructive' />
                            <div>
                              <p className='text-sm font-medium text-foreground'>
                                {image.filename}
                              </p>
                              <p className='text-xs text-destructive'>
                                {image.error || '压缩失败'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 使用说明 */}
            <div className='mt-8 p-6 rounded-2xl bg-muted/50 backdrop-blur-sm border border-border/50'>
              <h3 className='text-sm font-semibold text-foreground mb-3 flex items-center gap-2'>
                <div className='w-1.5 h-1.5 rounded-full bg-primary animate-pulse' />
                使用说明
              </h3>
              <ul className='space-y-2 text-sm text-muted-foreground'>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>
                    支持{' '}
                    <strong className='text-foreground'>
                      JPG、PNG、GIF、WebP、BMP
                    </strong>{' '}
                    等常见图片格式
                  </span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>
                    使用 <strong className='text-foreground'>TinyPNG</strong>{' '}
                    智能压缩算法，在保证图片质量的同时大幅减小文件大小
                  </span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>支持批量上传和处理，单次可处理多张图片</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>压缩完成后可单独下载或批量打包下载</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>
                    原图和压缩后的图片会
                    <strong className='text-foreground'>永久保存</strong>
                    在服务器的 temp/ai/tinify 目录下
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 预览弹窗 */}
        {previewImage && (
          <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in'
            onClick={() => setPreviewImage(null)}
          >
            <div className='relative max-w-4xl max-h-[90vh] p-4'>
              <button
                onClick={() => setPreviewImage(null)}
                className='absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200'
              >
                <X className='w-6 h-6' />
              </button>
              <img
                src={previewImage}
                alt='Preview'
                className='max-w-full max-h-[90vh] rounded-lg shadow-2xl'
              />
            </div>
          </div>
        )}
      </main>

      {/* 自定义动画样式 */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </>
  )
}
